import SectionHeading from "@/components/base/SectionHeading";
import PageHero from "@/components/feature/PageHero";
import LeadForm from "@/components/feature/LeadForm";
import StatStrip from "@/components/feature/StatStrip";
import { contactInfo } from "@/mocks/site";
import { stats, processSteps, whyChooseUs } from "@/mocks/home";
import { HERO_IMAGES } from "@/config/heroImages";

const QUOTE_FORM_ID = "quote-form";

export default function RequestAQuotePage() {
  return (
    <>
      <PageHero
        eyebrow="Free Estimate"
        title="Get A Quote"
        subtitle="Tell us what you are moving and we will send a transparent, item-wise estimate. No hidden costs, no obligation."
        crumbs={[{ label: "Home", href: "/" }, { label: "Get A Quote" }]}
        image={HERO_IMAGES.quote}
      />

      <section className="bg-background-50 py-14 md:py-16">
        <div className="mx-auto grid max-w-screen-2xl grid-cols-1 items-start gap-10 px-4 md:px-6 lg:grid-cols-[1.1fr_1fr] lg:gap-14">
          <div>
            <SectionHeading
              align="left"
              eyebrow="Request a Call Back"
              title="Get your free shifting quote in minutes"
              subtitle="Fill the form once and a Lahari coordinator calls you back with pricing, packing advice and available dates."
            />
            <div className="mt-6">
              <LeadForm
                formId={QUOTE_FORM_ID}
                title="Request a Free Quote"
                subtitle="Move your home, office or goods to storage with professional packers and movers in Bangalore."
                submitLabel="GET MY FREE QUOTE"
                serviceOptions={[
                  "House Shifting",
                  "Office / Commercial Shifting",
                  "Storage Service",
                  "Warehouse Facility",
                  "Packers Only (Packing & Unpacking)",
                ]}
              />
            </div>
          </div>

          <aside className="space-y-6">
            <div className="rounded-lg border border-background-200 bg-background-100 p-6">
              <h3 className="font-heading text-lg font-bold text-foreground-950">What to keep ready</h3>
              <ul className="mt-4 space-y-3">
                {[
                  "Your current address and the new address",
                  "Approximate number of rooms or workstations",
                  "Preferred moving date and time window",
                  "Any fragile, heavy or high value items",
                  "Lift availability at both locations",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-2.5 text-sm text-foreground-700">
                    <i className="ri-checkbox-circle-fill mt-0.5 text-primary-500" aria-hidden="true" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            <div className="rounded-lg border border-background-200 bg-accent-950 p-6">
              <h3 className="font-heading text-lg font-bold text-background-50">Talk to us instead</h3>
              <p className="mt-2 text-sm leading-relaxed text-background-200">
                Prefer a quick conversation? Our helpline answers 24 x 7 for quotes and shifting advice.
              </p>
              <a
                href={contactInfo.phoneHref}
                className="mt-4 flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-phone-fill text-lg" aria-hidden="true" /> {contactInfo.phone}
              </a>
              <a
                href={contactInfo.emailHref}
                className="mt-3 flex items-center justify-center gap-2 break-all whitespace-nowrap rounded-md border border-white/20 px-5 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-white/10"
              >
                <i className="ri-mail-line text-lg" aria-hidden="true" /> Email Us
              </a>
            </div>

            <div className="rounded-lg border border-background-200 bg-background-100 p-6">
              <h3 className="font-heading text-lg font-bold text-foreground-950">Why choose Lahari</h3>
              <ul className="mt-4 space-y-3">
                {whyChooseUs.slice(0, 4).map((item) => (
                  <li key={item.title} className="flex items-start gap-3">
                    <i className={`${item.icon} mt-0.5 text-lg text-primary-500`} aria-hidden="true" />
                    <span>
                      <span className="block text-sm font-semibold text-foreground-900">{item.title}</span>
                      <span className="block text-xs leading-relaxed text-foreground-600">{item.text}</span>
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </aside>
        </div>
      </section>

      <StatStrip stats={stats} />

      <section className="bg-background-50 py-14 md:py-16">
        <div className="mx-auto max-w-screen-2xl px-4 md:px-6">
          <SectionHeading eyebrow="After You Enquire" title="What happens next" />
          <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {processSteps.map((step, index) => (
              <div key={step.title} className="rounded-lg border border-background-200 bg-background-100 p-5 md:p-6">
                <span className="font-heading text-3xl font-extrabold text-primary-200">
                  {String(index + 1).padStart(2, "0")}
                </span>
                <h3 className="mt-2 font-heading text-base font-semibold text-foreground-950">{step.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">{step.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}